import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getLostAndFoundItemById, updateLostAndFoundItem } from '@/api/lostAndFound';
import { LostAndFoundItem } from '@/types/community/lostAndFound';
import { useAuth } from '@/contexts/AuthContext';
import LostAndFoundModal from '@/components/features/community/LostAndFoundModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommentsFormSection from '@/components/common/CommentsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteLostAndFoundItem } from '@/api/lostAndFound';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const itemStatusColors: Record<LostAndFoundItem['status'], string> = {
  found: 'blue',
  claimed: 'green',
  returned: 'purple',
  disposed: 'default'
};

const itemCategoryColors: Record<LostAndFoundItem['category'], string> = {
  electronics: 'blue',
  jewelry: 'gold',
  clothing: 'green',
  documents: 'purple',
  keys: 'orange',
  personal_items: 'cyan',
  other: 'default'
};

const itemConditionColors: Record<LostAndFoundItem['condition'], string> = {
  new: 'green',
  good: 'blue',
  fair: 'orange',
  poor: 'red',
  damaged: 'volcano',
  excellent: 'lime'
};

const LostAndFoundDetailsPage: React.FC = () => {
  const { t } = useTranslation(['lostAndFound', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { item: LostAndFoundItem } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - Always fetch to ensure we have the latest data
  const { data: item, isLoading, refetch } = useQuery({
    queryKey: ['lostAndFound', id],
    queryFn: () => getLostAndFoundItemById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser,
    staleTime: 0, // Always consider data stale to allow immediate refetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Use state data if available and more recent, otherwise use fetched data
  const displayItem = state?.item && !item ? state.item : item;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteLostAndFoundItem(itemId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('lostAndFound:itemDeleted'));
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/lost-and-found');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['lostAndFound', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Comment handlers
  const handleAddComment = async (comment: any) => {
    if (!displayItem || !firestoreUser) return;

    try {
      console.log('Adding comment:', comment);
      console.log('Current displayItem:', displayItem);
      
      const newComment = {
        ...comment,
        createdAt: new Date()
      };

      const updatedComments = [...(displayItem.comments || []), newComment];
      console.log('Updated comments:', updatedComments);

      await updateLostAndFoundItem(displayItem.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:messages.createSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['lostAndFound', id] });
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      await refetch();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      message.error(error.message || t('common:messages.createError'));
    }
  };

  const handleUpdateComment = async (commentIndex: number, content: string) => {
    if (!displayItem || !firestoreUser) return;

    try {
      const updatedComments = [...(displayItem.comments || [])];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],
        content
      };

      await updateLostAndFoundItem(displayItem.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:messages.updateSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['lostAndFound', id] });
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      await refetch();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      message.error(error.message || t('common:messages.updateError'));
    }
  };

  const handleDeleteComment = async (commentIndex: number) => {
    if (!displayItem || !firestoreUser) return;

    try {
      const updatedComments = [...(displayItem.comments || [])];
      updatedComments.splice(commentIndex, 1);

      await updateLostAndFoundItem(displayItem.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:messages.deleteSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['lostAndFound', id] });
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      await refetch();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      message.error(error.message || t('common:messages.deleteError'));
    }
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.item) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayItem) {
    return <Card>Item not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayItem.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content */}
      <Card>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('lostAndFound:table.title')}>
            <Text strong>{displayItem.title}</Text>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('lostAndFound:table.description')}>
            <Paragraph>{displayItem.description}</Paragraph>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('lostAndFound:table.category')}>
            <Tag color={itemCategoryColors[displayItem.category]}>
              {t(`lostAndFound:categories.${displayItem.category}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('lostAndFound:table.status')}>
            <Tag color={itemStatusColors[displayItem.status]}>
              {t(`lostAndFound:statuses.${displayItem.status}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('lostAndFound:table.condition')}>
            <Tag color={itemConditionColors[displayItem.condition]}>
              {t(`lostAndFound:conditions.${displayItem.condition}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('lostAndFound:table.foundAt')}>
            {dayjs(displayItem.foundAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
          
          {displayItem.returnedAt && (
            <Descriptions.Item label={t('lostAndFound:table.returnedAt')}>
              {dayjs(displayItem.returnedAt).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          )}
          
          {displayItem.block && (
            <Descriptions.Item label={t('lostAndFound:table.block')}>
              {displayItem.block.name}
            </Descriptions.Item>
          )}
          
          {displayItem.apartmentUnit && (
            <Descriptions.Item label={t('lostAndFound:table.apartmentUnit')}>
              {displayItem.apartmentUnit.name}
            </Descriptions.Item>
          )}
          
          {displayItem.claimedBy && (
            <Descriptions.Item label={t('lostAndFound:table.claimedBy')}>
              {displayItem.claimedBy.name} ({displayItem.claimedBy.type ? t(`lostAndFound:personTypes.${displayItem.claimedBy.type}`) : t('common:none')})
            </Descriptions.Item>
          )}
          
          {displayItem.reportedBy && (
            <Descriptions.Item label={t('lostAndFound:table.reportedBy')}>
              {displayItem.reportedBy.name} ({displayItem.reportedBy.type ? t(`lostAndFound:personTypes.${displayItem.reportedBy.type}`) : t('common:none')})
            </Descriptions.Item>
          )}
          
          {displayItem.handedOverTo && (
            <Descriptions.Item label={t('lostAndFound:table.handedOverTo')}>
              {displayItem.handedOverTo.name}
            </Descriptions.Item>
          )}
          
          {displayItem.value && (
            <Descriptions.Item label={t('lostAndFound:table.value')}>
              {displayItem.value.amount} {displayItem.value.currency}
            </Descriptions.Item>
          )}
          
          {displayItem.photos && displayItem.photos.length > 0 && (
            <Descriptions.Item label={t('lostAndFound:table.photos')}>
              {displayItem.photos.length} {t('common:items')}
            </Descriptions.Item>
          )}
        </Descriptions>
        
        {/* Comments Section */}
        <div style={{ marginTop: 24 }}>
          <Typography.Title level={4}>{t('common:comments')}</Typography.Title>
          <CommentsFormSection
            comments={displayItem.comments || []}
            onAddComment={handleAddComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            showForm={true}
            maxHeight="400px"
          />
        </div>
      </Card>

      {/* Edit Modal */}
      <LostAndFoundModal
        open={isEditModalOpen}
        item={displayItem}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('lostAndFound:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default LostAndFoundDetailsPage; 